import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Search, Package, Pencil, Trash2, Eye, EyeOff, Upload, X,
  GripVertical, ChevronUp, ChevronDown, Loader2, Tag, Box, Download, Briefcase, Gift, Truck
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

// Image uploader with drag & drop
const ProductImageUploader = ({ images = [], onChange, maxImages = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const newImages = [...images];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      if (newImages.length >= maxImages) break;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API}/media/upload?category=products`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        newImages.push(process.env.REACT_APP_BACKEND_URL + res.data.url);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    onChange(newImages);
    setUploading(false);
  };

  const handleDrop = (e) => { e.preventDefault(); handleUpload(e.dataTransfer?.files); };
  const removeImage = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <Label className="text-[#94A3B8] text-xs">Images ({images.length}/{maxImages})</Label>
      <div className="grid grid-cols-4 gap-2">
        {images.map((img, idx) => (
          <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-white/10">
            <img src={img} alt="" className="w-full h-full object-cover" />
            <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} className="text-white" /></button>
          </div>
        ))}
        {images.length < maxImages && (
          <div
            className="aspect-square border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#8B5CF6]/40 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            data-testid="product-image-upload"
          >
            {uploading ? <Loader2 size={20} className="animate-spin text-[#8B5CF6]" /> : <><Upload size={18} className="text-[#64748B] mb-1" /><span className="text-[10px] text-[#64748B]">Upload</span></>}
          </div>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
    </div>
  );
};

// Size/Color variant row
const VariantEditor = ({ variants = [], onChange, type }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-[#94A3B8] text-xs">{type === 'size' ? 'Size Variants' : 'Color Variants'}</Label>
      <button onClick={() => onChange([...variants, { name: '', price: '', in_stock: true }])} className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1 transition-colors" data-testid={`add-${type}-variant`}>
        <Plus size={12} /> Add
      </button>
    </div>
    {variants.map((v, i) => (
      <div key={i} className="flex items-center gap-2">
        <Input value={v.name} onChange={(e) => { const nv = [...variants]; nv[i] = { ...nv[i], name: e.target.value }; onChange(nv); }} placeholder={type === 'size' ? 'S, M, L, XL' : 'Red, Blue, Black'} className="bg-white/5 border-white/10 text-white text-xs h-8 flex-1 rounded-lg" />
        <div className="relative w-28">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#64748B] text-xs">$</span>
          <Input type="number" step="0.01" value={v.price} onChange={(e) => { const nv = [...variants]; nv[i] = { ...nv[i], price: e.target.value }; onChange(nv); }} placeholder="0.00" className="bg-white/5 border-white/10 text-white text-xs h-8 pl-5 rounded-lg" />
        </div>
        {type === 'color' && (
          <input type="color" value={v.hex || '#ffffff'} onChange={(e) => { const nv = [...variants]; nv[i] = { ...nv[i], hex: e.target.value }; onChange(nv); }} className="w-8 h-8 rounded border-0 bg-transparent cursor-pointer" />
        )}
        <button onClick={() => onChange(variants.filter((_, idx) => idx !== i))} className="text-[#64748B] hover:text-[#FF6B6B] shrink-0"><Trash2 size={12} /></button>
      </div>
    ))}
  </div>
);

/* ────────────────────────────────────────────────────────────
 *  OptionsBlock — Shopify-style up-to-3 variants matrix.
 *  Each option = { name, values: string[] }. Stored on the product
 *  as `options` (replaces standalone sizes/colors). Combinations
 *  inherit the base price.
 * ──────────────────────────────────────────────────────────── */
const OPTION_PRESETS = ['Size', 'Color', 'Material', 'Style', 'Length', 'Pattern'];
const OptionsBlock = ({ options = [], onChange }) => {
  const addOption = () => {
    if (options.length >= 3) return;
    const used = options.map((o) => o.name);
    const preset = OPTION_PRESETS.find((p) => !used.includes(p)) || '';
    onChange([...options, { name: preset, values: [] }]);
  };
  const updateOption = (idx, patch) => {
    const next = [...options];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeOption = (idx) => onChange(options.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3 p-4 rounded-xl bg-white/[0.025] border border-white/[0.06]">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-[#94A3B8] text-sm font-bold">Variants</Label>
          <p className="text-[10px] text-[#64748B] mt-0.5">Add up to 3 options like Size, Color, or Material.</p>
        </div>
        <button
          type="button"
          onClick={addOption}
          disabled={options.length >= 3}
          className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="add-option"
        >
          <Plus size={12} /> Add option
        </button>
      </div>
      {options.map((opt, i) => (
        <div key={i} className="p-3 rounded-lg bg-[#0E0E23] border border-white/[0.06]" data-testid={`option-row-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-bold">Option name</Label>
              <Input
                list={`option-presets-${i}`}
                value={opt.name}
                onChange={(e) => updateOption(i, { name: e.target.value })}
                placeholder="Size"
                className="mt-1 bg-white/5 border-white/10 text-white text-sm rounded-lg"
                data-testid={`option-name-${i}`}
              />
              <datalist id={`option-presets-${i}`}>
                {OPTION_PRESETS.map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
            <button
              type="button"
              onClick={() => removeOption(i)}
              className="self-end mb-1 text-[#64748B] hover:text-[#FB7185] p-1"
              aria-label="Remove option"
              data-testid={`remove-option-${i}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-[#7B7FA8] font-bold">Option values</Label>
          <div className="mt-1 flex flex-wrap gap-2 p-2 rounded-lg bg-white/[0.025] border border-white/[0.06] min-h-[44px]">
            {opt.values.map((val, vi) => (
              <span
                key={vi}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 text-white"
                data-testid={`option-value-${i}-${vi}`}
              >
                {val}
                <button
                  type="button"
                  onClick={() => updateOption(i, { values: opt.values.filter((_, idx) => idx !== vi) })}
                  className="text-white/55 hover:text-white"
                  aria-label="Remove value"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
            <input
              type="text"
              placeholder="Add value, press Enter"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                  e.preventDefault();
                  updateOption(i, { values: [...opt.values, e.currentTarget.value.trim()] });
                  e.currentTarget.value = '';
                }
              }}
              className="bg-transparent text-white text-xs outline-none flex-1 min-w-[120px] placeholder:text-[#64748B]"
              data-testid={`option-value-input-${i}`}
            />
          </div>
        </div>
      ))}
      {options.length === 0 && (
        <p className="text-[11px] text-[#7B7FA8] text-center py-2">
          No variants yet. Click "Add option" to start.
        </p>
      )}
    </div>
  );
};

const _OptionsBlockUsedMarker = OptionsBlock;

const PRODUCT_TYPES = [
  { id: 'physical', label: 'Physical', icon: Box, desc: 'Shipped to customer' },
  { id: 'digital', label: 'Digital', icon: Download, desc: 'Downloadable file' },
  { id: 'service', label: 'Service', icon: Briefcase, desc: 'Bookable service' },
  { id: 'gift_card', label: 'Gift Card', icon: Gift, desc: 'Store credit' },
];

const TYPE_COLORS = {
  physical: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  digital: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  service: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  gift_card: { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/20' },
};

const defaultProduct = {
  name: '', description: '', price: '', compare_at_price: '',
  sku: '', cta_text: 'Add to Cart',
  images: [], category: '', sizes: [], colors: [], options: [],
  is_active: true, is_featured: true, display_order: 0,
  product_type: 'physical', download_url: '', file_name: '',
  duration: '', booking_link: '', denominations: []
};

export default function DashboardProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...defaultProduct });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, { headers });
      const prods = res.data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setProducts(prods);
      const cats = [...new Set(prods.map(p => p.category).filter(Boolean))];
      setCategories(cats);
    } catch {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); fetchReadiness(); }, []);
  const [readiness, setReadiness] = useState(null);
  const fetchReadiness = async () => {
    try {
      const r = await axios.get(`${API}/completion/vendor`, { headers });
      setReadiness(r.data);
    } catch { /* non-blocking */ }
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ ...defaultProduct });
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditId(product.id);
    setForm({
      name: product.name || '', description: product.description || '',
      price: product.price?.toString() || '', compare_at_price: product.compare_at_price?.toString() || '',
      sku: product.sku || '',
      cta_text: product.cta_text || 'Add to Cart',
      images: product.images || (product.image_url ? [product.image_url] : []),
      category: product.category || '', sizes: product.sizes || [], colors: product.colors || [], options: product.options || [],
      is_active: product.is_active !== false, is_featured: product.is_featured !== false,
      display_order: product.display_order || 0,
      product_type: product.product_type || 'physical',
      download_url: product.download_url || '', file_name: product.file_name || '',
      duration: product.duration?.toString() || '', booking_link: product.booking_link || '',
      denominations: product.denominations || []
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error('Name and price are required'); return; }
    setSaving(true);
    const payload = {
      ...form, price: parseFloat(form.price),
      compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
      image_url: form.images[0] || '',
      duration: form.duration ? parseInt(form.duration) : null,
    };
    try {
      if (editId) {
        await axios.put(`${API}/products/${editId}`, payload, { headers });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, payload, { headers });
        toast.success('Product added');
      }
      setShowForm(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, { is_active: !product.is_active }, { headers });
      toast.success(product.is_active ? 'Product deactivated' : 'Product activated');
      fetchProducts();
    } catch { toast.error('Failed to update product'); }
  };

  const deleteProduct = async (id) => {
    if (!(await themedConfirm('Delete this product?'))) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers });
      toast.success('Product deleted');
      fetchProducts();
    } catch { toast.error('Failed to delete'); }
  };

  const moveProduct = async (index, direction) => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= products.length) return;
    const reordered = [...products];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    reordered.forEach((p, i) => p.display_order = i);
    setProducts(reordered);
    try {
      await Promise.all(reordered.map((p, i) =>
        axios.put(`${API}/products/${p.id}`, { display_order: i }, { headers })
      ));
    } catch { /* silent */ }
  };

  const addCategory = () => {
    if (newCategory && !categories.includes(newCategory)) {
      setCategories([...categories, newCategory]);
      setForm(p => ({ ...p, category: newCategory }));
      setNewCategory('');
    }
  };

  const filtered = products.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()) || p.category?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  return (
    <div className="space-y-6" data-testid="dashboard-products">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="products-title">Products</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">{products.length} products</p>
        </div>
        {readiness?.ready_to_sell === false ? (
          <button onClick={() => toast.error('Finish your profile first before listing products.')} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-white/5 border border-white/10 text-white/60 cursor-not-allowed" data-testid="add-product-btn-locked" title="Finish profile setup to list products">
            <Plus size={16} /> Add Product
          </button>
        ) : (
          <button onClick={openAdd} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all" data-testid="add-product-btn">
            <Plus size={16} /> Add Product
          </button>
        )}
      </div>

      {readiness?.ready_to_sell === false && (
        <div className="bg-amber-500/10 border border-amber-400/30 rounded-2xl p-4 text-sm text-amber-100 flex items-center justify-between gap-4" data-testid="products-gate-banner">
          <div>
            <p className="font-bold uppercase tracking-wider text-xs text-amber-200">Profile setup required</p>
            <p className="mt-1">You're {readiness.percentage}% set up. Finish your profile to start listing products.</p>
          </div>
          <a href="/dashboard/vendor/profile" className="flex-shrink-0 px-4 py-2 rounded-lg bg-white text-[#0E0E23] text-xs font-bold uppercase tracking-wider" data-testid="products-gate-continue-btn">Continue setup</a>
        </div>
      )}

      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="search-products" />
      </div>

      {/* Products Grid */}
      {filtered.length > 0 ? (
        <div className="space-y-3" data-testid="products-list">
          {filtered.map((product, idx) => (
            <GlassCard key={product.id} className={`p-4 flex items-center gap-4 ${!product.is_active ? 'opacity-50' : ''} hover:border-[#8B5CF6]/20 transition-all`} data-testid={`product-card-${product.id}`}>
              {/* Reorder */}
              <div className="flex flex-col gap-0.5 shrink-0">
                <button onClick={() => moveProduct(idx, 'up')} disabled={idx === 0} className="text-[#64748B] hover:text-white disabled:opacity-20"><ChevronUp size={14} /></button>
                <GripVertical size={14} className="text-[#64748B] mx-auto" />
                <button onClick={() => moveProduct(idx, 'down')} disabled={idx === filtered.length - 1} className="text-[#64748B] hover:text-white disabled:opacity-20"><ChevronDown size={14} /></button>
              </div>

              {/* Image */}
              <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-white/5">
                {(product.images?.[0] || product.image_url) ? (
                  <img src={product.images?.[0] || product.image_url} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Package size={20} className="text-[#64748B]" /></div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-white truncate">{product.name}</p>
                  {product.product_type && product.product_type !== 'physical' && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_COLORS[product.product_type]?.bg || ''} ${TYPE_COLORS[product.product_type]?.text || 'text-slate-400'} ${TYPE_COLORS[product.product_type]?.border || 'border-white/10'}`}>
                      {PRODUCT_TYPES.find(t => t.id === product.product_type)?.label || product.product_type}
                    </span>
                  )}
                  {product.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#A78BFA] border border-[#8B5CF6]/20">{product.category}</span>}
                  {!product.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-[#FF6B6B] border border-red-500/20">Inactive</span>}
                </div>
                <p className="text-xs text-[#64748B] mt-0.5 truncate">{product.description?.substring(0, 80)}</p>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-sm text-[#00CC99] font-semibold">${product.price?.toFixed(2)}</p>
                  {product.compare_at_price && <p className="text-xs text-[#64748B] line-through">${product.compare_at_price?.toFixed(2)}</p>}
                  {product.sizes?.length > 0 && <span className="text-[10px] text-[#64748B]">{product.sizes.length} sizes</span>}
                  {product.colors?.length > 0 && <span className="text-[10px] text-[#64748B]">{product.colors.length} colors</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleActive(product)} className={`p-2 rounded-lg transition-colors ${product.is_active ? 'text-[#00CC99] hover:bg-[#00CC99]/10' : 'text-[#64748B] hover:bg-white/5'}`} title={product.is_active ? 'Active' : 'Inactive'} data-testid={`toggle-active-${product.id}`}>
                  {product.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => openEdit(product)} className="p-2 text-[#9CA3AF] hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid={`edit-product-${product.id}`}>
                  <Pencil size={16} />
                </button>
                <button onClick={() => deleteProduct(product.id)} className="p-2 text-[#64748B] hover:text-[#FF6B6B] hover:bg-red-500/5 rounded-lg transition-colors" data-testid={`delete-product-${product.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard className="text-center py-16">
          <Package size={48} className="mx-auto text-[#64748B] mb-4" />
          <h3 className="font-semibold text-lg text-white mb-2">No products yet</h3>
          <p className="text-[#9CA3AF] text-sm mb-6">Add your first product to get started.</p>
          <button onClick={openAdd} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] transition-all"><Plus size={16} /> Add Product</button>
        </GlassCard>
      )}

      {/* Add/Edit Product — Shopify-style 2-column fullscreen overlay */}
      {showForm && (
        <div className="fixed inset-0 z-[180] bg-[#0A0A1F] overflow-y-auto" data-testid="product-form-overlay">
          {/* Sticky top bar */}
          <div className="sticky top-0 z-10 bg-[#0A0A1F]/95 backdrop-blur-xl border-b border-white/[0.06] px-6 py-4 flex items-center justify-between" data-testid="product-form-topbar">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="p-2 rounded-lg hover:bg-white/[0.06] text-[#9CA3AF] hover:text-white transition-colors shrink-0"
                aria-label="Back to products"
                data-testid="product-form-back"
              >
                <X size={18} />
              </button>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{editId ? 'Edit product' : 'New product'}</p>
                <h2 className="text-lg font-bold text-white truncate" data-testid="product-form-title">{editId ? form.name || 'Edit Product' : 'Add Product'}</h2>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-[#9CA3AF] hover:bg-white/[0.06] hover:text-white transition-colors"
                data-testid="product-form-discard"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
                data-testid="save-product-btn"
              >
                {saving ? 'Saving…' : editId ? 'Save' : 'Save product'}
              </button>
            </div>
          </div>

          {/* 2-column body */}
          <form onSubmit={handleSave} className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="product-form">
            {/* LEFT — main content (2/3) */}
            <div className="lg:col-span-2 space-y-5">
              {/* Title + description */}
              <GlassCard className="p-5">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold">Title *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Premium Cotton T-Shirt"
                  required
                  className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl"
                  data-testid="product-name-input"
                />
                <div className="mt-4">
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold">Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Describe your product…"
                    rows={5}
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl"
                    data-testid="product-desc-input"
                  />
                </div>
              </GlassCard>

              {/* Media */}
              <GlassCard className="p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Media</p>
                <ProductImageUploader images={form.images} onChange={(imgs) => setForm(p => ({ ...p, images: imgs }))} />
              </GlassCard>

              {/* Pricing */}
              <GlassCard className="p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Pricing</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Price *</Label>
                    <div className="relative mt-1.5">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
                      <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))} required className="bg-white/5 border-white/10 text-white pl-7 rounded-xl" data-testid="product-price-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Compare-at price</Label>
                    <div className="relative mt-1.5">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
                      <Input type="number" step="0.01" value={form.compare_at_price} onChange={(e) => setForm(p => ({ ...p, compare_at_price: e.target.value }))} className="bg-white/5 border-white/10 text-white pl-7 rounded-xl" />
                    </div>
                  </div>
                </div>
              </GlassCard>

              {/* Variants */}
              {(form.product_type === 'physical' || form.product_type === 'gift_card') && (
                <GlassCard className="p-5">
                  <OptionsBlock options={form.options} onChange={(opts) => setForm(p => ({ ...p, options: opts }))} />
                </GlassCard>
              )}

              {/* Shipping (weight + dimensions) — required for shipping-quote math */}
              {form.product_type === 'physical' && (
                <GlassCard className="p-5" data-testid="product-shipping-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Truck size={12} className="text-[#A78BFA]" />
                    <p className="text-xs font-semibold text-white">Shipping</p>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-3">
                    Enter the packaged weight &amp; dimensions so we can quote real carrier rates at checkout.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#94A3B8] text-xs">Weight</Label>
                      <div className="mt-1 flex gap-1.5">
                        <Input type="number" step="0.01" min="0"
                          value={form.weight ?? ''}
                          onChange={(e) => setForm(p => ({ ...p, weight: e.target.value }))}
                          placeholder="0.00"
                          className="bg-white/5 border-white/10 text-white rounded-xl text-sm"
                          data-testid="product-weight" />
                        <select
                          value={form.weight_unit || 'g'}
                          onChange={(e) => setForm(p => ({ ...p, weight_unit: e.target.value }))}
                          className="w-16 bg-white/5 border border-white/10 rounded-xl text-white text-xs px-1 focus:outline-none focus:border-[#A78BFA]/60"
                          data-testid="product-weight-unit">
                          <option value="g">g</option><option value="kg">kg</option>
                          <option value="oz">oz</option><option value="lb">lb</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#94A3B8] text-xs">Dimensions (L × W × H)</Label>
                      <div className="mt-1 flex gap-1">
                        <Input type="number" step="0.1" min="0"
                          value={form.length ?? ''}
                          onChange={(e) => setForm(p => ({ ...p, length: e.target.value }))}
                          placeholder="L"
                          className="bg-white/5 border-white/10 text-white rounded-xl text-sm"
                          data-testid="product-length" />
                        <Input type="number" step="0.1" min="0"
                          value={form.width ?? ''}
                          onChange={(e) => setForm(p => ({ ...p, width: e.target.value }))}
                          placeholder="W"
                          className="bg-white/5 border-white/10 text-white rounded-xl text-sm"
                          data-testid="product-width" />
                        <Input type="number" step="0.1" min="0"
                          value={form.height ?? ''}
                          onChange={(e) => setForm(p => ({ ...p, height: e.target.value }))}
                          placeholder="H"
                          className="bg-white/5 border-white/10 text-white rounded-xl text-sm"
                          data-testid="product-height" />
                        <select
                          value={form.dimension_unit || 'cm'}
                          onChange={(e) => setForm(p => ({ ...p, dimension_unit: e.target.value }))}
                          className="w-16 bg-white/5 border border-white/10 rounded-xl text-white text-xs px-1 focus:outline-none focus:border-[#A78BFA]/60"
                          data-testid="product-dim-unit">
                          <option value="cm">cm</option><option value="in">in</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-[10.5px] text-white/40">
                    Leave blank to fall back to your store&apos;s default package preset (Settings → Shipping).
                  </p>
                </GlassCard>
              )}

              {/* Digital Product Fields */}
              {form.product_type === 'digital' && (
                <GlassCard className="p-5">
                  <p className="text-xs text-purple-400 font-medium flex items-center gap-1.5 mb-3"><Download size={12} /> Digital Product Settings</p>
                  <div className="space-y-3">
                    <div><Label className="text-[#94A3B8] text-xs">Download File URL</Label><Input value={form.download_url} onChange={(e) => setForm(p => ({ ...p, download_url: e.target.value }))} placeholder="https://… or upload via Media" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="product-download-url" /></div>
                    <div><Label className="text-[#94A3B8] text-xs">File Name (shown to customer)</Label><Input value={form.file_name} onChange={(e) => setForm(p => ({ ...p, file_name: e.target.value }))} placeholder="e.g., Brand-Guide-v2.pdf" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="product-file-name" /></div>
                  </div>
                </GlassCard>
              )}

              {/* Service Fields */}
              {form.product_type === 'service' && (
                <GlassCard className="p-5">
                  <p className="text-xs text-amber-400 font-medium flex items-center gap-1.5 mb-3"><Briefcase size={12} /> Service Settings</p>
                  <div className="space-y-3">
                    <div><Label className="text-[#94A3B8] text-xs">Duration (minutes)</Label><Input type="number" value={form.duration} onChange={(e) => setForm(p => ({ ...p, duration: e.target.value }))} placeholder="60" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="product-duration" /></div>
                    <div><Label className="text-[#94A3B8] text-xs">Booking Link (optional)</Label><Input value={form.booking_link} onChange={(e) => setForm(p => ({ ...p, booking_link: e.target.value }))} placeholder="Link to your booking page" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="product-booking-link" /></div>
                  </div>
                </GlassCard>
              )}

              {/* Gift Card Fields */}
              {form.product_type === 'gift_card' && (
                <GlassCard className="p-5">
                  <p className="text-xs text-pink-400 font-medium flex items-center gap-1.5 mb-2"><Gift size={12} /> Gift Card Denominations</p>
                  <p className="text-[11px] text-slate-500 mb-3">The Price field above sets the default value. Add extra denominations below.</p>
                  <div className="flex flex-wrap gap-2">
                    {(form.denominations || []).map((d, i) => (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded-lg border border-white/10">
                        <span className="text-xs text-white">${d}</span>
                        <button type="button" onClick={() => setForm(p => ({ ...p, denominations: p.denominations.filter((_, idx) => idx !== i) }))} className="text-slate-500 hover:text-[#FF6B6B]"><X size={10} /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => {
                      const val = prompt('Enter denomination amount:');
                      if (val && !isNaN(val)) setForm(p => ({ ...p, denominations: [...(p.denominations || []), parseFloat(val)] }));
                    }} className="text-xs text-pink-400 px-2 py-1 border border-dashed border-pink-500/20 rounded-lg hover:bg-pink-500/5" data-testid="add-denomination">+ Add</button>
                  </div>
                </GlassCard>
              )}
            </div>

            {/* RIGHT — sidebar (1/3) */}
            <aside className="space-y-5 lg:sticky lg:top-[88px] self-start" data-testid="product-form-sidebar">
              {/* Status */}
              <GlassCard className="p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Status</p>
                <div className="space-y-2.5">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-white">Active on website</span>
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))} className="accent-[#8B5CF6]" data-testid="product-active-toggle" />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-white">Show on homepage</span>
                    <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm(p => ({ ...p, is_featured: e.target.checked }))} className="accent-[#8B5CF6]" data-testid="product-featured-toggle" />
                  </label>
                </div>
              </GlassCard>

              {/* Product type */}
              <GlassCard className="p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Product type</p>
                <div className="grid grid-cols-2 gap-2" data-testid="product-type-selector">
                  {PRODUCT_TYPES.map(pt => {
                    const Icon = pt.icon;
                    const active = form.product_type === pt.id;
                    return (
                      <button key={pt.id} type="button" onClick={() => setForm(p => ({ ...p, product_type: pt.id }))}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all text-center ${active ? `${TYPE_COLORS[pt.id].bg} ${TYPE_COLORS[pt.id].border} ${TYPE_COLORS[pt.id].text}` : 'bg-white/5 border-white/10 text-slate-400 hover:border-white/20'}`}
                        data-testid={`product-type-${pt.id}`}
                      >
                        <Icon size={18} />
                        <span className="text-xs font-medium">{pt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </GlassCard>

              {/* Organization (category) */}
              <GlassCard className="p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3 flex items-center gap-1.5"><Tag size={11} /> Organization</p>
                <Label className="text-[#94A3B8] text-xs">Category</Label>
                <select value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} className="mt-1.5 w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm" data-testid="product-category">
                  <option value="">No category</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <div className="mt-2 flex gap-1.5">
                  <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category" className="bg-white/5 border-white/10 text-white text-xs rounded-lg flex-1" data-testid="new-category-input" />
                  <button type="button" onClick={addCategory} className="px-3 py-1 bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/20 rounded-lg text-xs hover:bg-[#8B5CF6]/30 transition-colors" data-testid="add-category-btn">Add</button>
                </div>
              </GlassCard>

              {/* Inventory + CTA */}
              <GlassCard className="p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-3">Inventory</p>
                <Label className="text-[#94A3B8] text-xs">SKU</Label>
                <Input value={form.sku || ''} onChange={(e) => setForm(p => ({ ...p, sku: e.target.value }))} placeholder="e.g. BEAD-001" className="mt-1.5 bg-white/[0.04] border-white/10 text-white" data-testid="product-sku" />
                <Label className="text-[#94A3B8] text-xs mt-4 block">Button text</Label>
                <Input value={form.cta_text || 'Add to Cart'} onChange={(e) => setForm(p => ({ ...p, cta_text: e.target.value }))} placeholder="Add to Cart" className="mt-1.5 bg-white/[0.04] border-white/10 text-white" data-testid="product-cta-text" />
              </GlassCard>

              {/* Mobile save button */}
              <div className="lg:hidden">
                <button type="submit" disabled={saving} className="w-full py-3 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all">
                  {saving ? 'Saving…' : editId ? 'Save Changes' : 'Add Product'}
                </button>
              </div>
            </aside>

            {/* Hidden submit input so Enter key still submits */}
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
          </form>
        </div>
      )}
    </div>
  );
}
